import React, { useState } from 'react';
import {
  Check,
  ChevronRight,
  Layers,
  PenTool,
  Monitor,
  Lightbulb,
  Settings,
  FileText,
  Cpu,
  Wifi,
  ArrowRight,
  Box,
  Award,
  Info
} from 'lucide-react';

const COLORS = {
  navy: '#101725',
  navyDark: '#070B14',
  orange: '#E58A3A',
  orangeHover: '#F19A45',
  white: '#FFFFFF',
  cream: '#F7F4EF',
  textGray: '#9CA3AF',
  borderDark: '#1F2937',
  borderLight: '#E5E7EB'
};

const matrixData = [
  {
    name: 'Architectural Drafting',
    checks: [true, true, true, true],
    details: ['Floor plans', 'Elevations', 'Sections', 'Reflected ceiling plans', 'Millwork details'],
    tools: [
      { name: 'AutoCAD', icon: PenTool },
      { name: 'Revit', icon: Box },
      { name: 'SketchUp', icon: Layers }
    ]
  },
  {
    name: 'Low Voltage',
    checks: [true, true, true, true],
    details: ['Device locations', 'Cable pathways', 'System coordination', 'Panel schedules'],
    tools: [
      { name: 'AutoCAD', icon: PenTool },
      { name: 'Revit', icon: Box },
      { name: 'D-Tools', icon: Settings }
    ]
  },
  {
    name: 'Audio / Video',
    checks: [true, true, true, true],
    details: ['System layouts', 'Device locations', 'Equipment documentation', 'Signal flow', 'Rack elevations'],
    tools: [
      { name: 'AutoCAD', icon: PenTool },
      { name: 'D-Tools', icon: Settings },
      { name: 'Visio', icon: Monitor }
    ]
  },
  {
    name: 'Networking',
    checks: [true, true, true, true],
    details: ['WAP coverage maps', 'Switch topologies', 'VLAN planning', 'Jack locations'],
    tools: [
      { name: 'AutoCAD', icon: PenTool },
      { name: 'Ekahau', icon: Wifi },
      { name: 'D-Tools', icon: Settings }
    ]
  },
  {
    name: 'Security',
    checks: [true, true, true, true],
    details: ['Camera placement', 'Field of view analysis', 'Access control doors', 'Security schematics'],
    tools: [
      { name: 'AutoCAD', icon: PenTool },
      { name: 'Revit', icon: Box },
      { name: 'Bluebeam', icon: FileText }
    ]
  },
  {
    name: 'Lighting / Controls',
    checks: [true, true, true, true],
    details: ['Fixture schedules', 'Load schedules', 'Control wiring', 'Keypad engraving docs'],
    tools: [
      { name: 'AutoCAD', icon: PenTool },
      { name: 'DIALux', icon: Lightbulb },
      { name: 'Lutron D.', icon: Cpu }
    ]
  },
  {
    name: 'Takeoffs',
    checks: [false, true, true, true],
    details: ['Material counts', 'Wire length estimates', 'Labor time approximations', 'BOM generation'],
    tools: [
      { name: 'Bluebeam', icon: FileText },
      { name: 'D-Tools', icon: Settings }
    ]
  },
  {
    name: 'As-Builts',
    checks: [false, true, true, true],
    details: ['Redline incorporation', 'Final wire paths', 'Actual IP assignments', 'Project handover docs'],
    tools: [
      { name: 'AutoCAD', icon: PenTool },
      { name: 'Revit', icon: Box },
      { name: 'Bluebeam', icon: FileText }
    ]
  },
];

const portfolioItems = [
  { id: 1, category: 'Architectural', title: 'High-End Residential Plan' },
  { id: 2, category: 'Low Voltage', title: 'Corporate HQ Wiring' },
  { id: 3, category: 'Schematics', title: 'AV Signal Flow Diagram' },
  { id: 4, category: 'Lighting', title: 'Lighting Control Layout' },
  { id: 5, category: 'Network', title: 'Campus WAP Heatmap' },
  { id: 6, category: '3D', title: 'Rack Elevation Render' },
];

const HeroSection = () => {
  return (
    <section className="relative overflow-hidden bg-[#101725] text-white pt-32 pb-24 lg:pt-40 lg:pb-32">
      {/* Abstract Background Blueprint Lines */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="w-full h-full" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-[1px] bg-[#E58A3A]"></div>
            <span className="text-[#E58A3A] text-sm font-semibold tracking-widest uppercase">Skills & Capabilities</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-light tracking-tight mb-8 leading-tight">
            Precision Behind Every <br />
            <span className="text-[#E58A3A] font-medium">Connected Space.</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-xl leading-relaxed border-l-2 border-gray-800 pl-6">
            From architectural drafting and low-voltage design to takeoffs, documentation and technical coordination, DMI transforms project requirements into clear, accurate and build-ready information.
          </p>
        </div>

        {/* Layered Technical Visual */}
        <div className="relative h-[400px] w-full hidden lg:block perspective-1000">
          <div className="absolute inset-0 flex flex-col items-center justify-center transform -rotate-y-12 rotate-x-12">
            {/* Layer 1 */}
            <div className="relative w-3/4 h-48 border border-gray-600 bg-[#101725]/80 backdrop-blur-sm rounded-lg transform translate-y-[-60px] translate-x-[-20px] shadow-[0_0_30px_rgba(229,138,58,0.1)] flex items-center justify-center transition-transform hover:translate-y-[-70px] duration-500">
              <div className="absolute top-2 left-2 text-[10px] tracking-widest text-gray-500 uppercase">Architectural Plan</div>
              <div className="w-full h-full opacity-20" style={{ backgroundImage: `repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)`, backgroundSize: '10px 10px' }}></div>
              <div className="absolute -right-4 top-1/2 bg-[#E58A3A] text-[#101725] text-[10px] font-bold px-2 py-1 rounded shadow-lg">LIGHTING</div>
            </div>

            {/* Layer 2 */}
            <div className="absolute w-3/4 h-48 border border-[#E58A3A]/50 bg-[#101725]/90 backdrop-blur-md rounded-lg transform translate-y-[0px] shadow-[0_20px_40px_rgba(0,0,0,0.5)] flex items-center justify-center transition-transform hover:translate-y-[-10px] duration-500 z-10">
              <div className="absolute top-2 left-2 text-[10px] tracking-widest text-[#E58A3A] uppercase">Low Voltage Plan</div>
              <div className="w-1/2 h-1/2 border border-[#E58A3A]/30 rounded-full flex items-center justify-center relative">
                <div className="w-2 h-2 bg-[#E58A3A] rounded-full animate-pulse"></div>
                {/* Connection lines */}
                <div className="absolute top-0 right-full w-12 h-[1px] bg-[#E58A3A]/50"></div>
                <div className="absolute bottom-0 left-full w-12 h-[1px] bg-[#E58A3A]/50"></div>
              </div>
              <div className="absolute -left-6 bottom-4 bg-[#E58A3A] text-[#101725] text-[10px] font-bold px-2 py-1 rounded shadow-lg z-20">NETWORK</div>
              <div className="absolute -right-2 bottom-8 bg-white text-[#101725] text-[10px] font-bold px-2 py-1 rounded shadow-lg z-20">A/V</div>
            </div>

            {/* Layer 3 */}
            <div className="absolute w-3/4 h-48 border border-gray-700 bg-[#070B14]/90 backdrop-blur-lg rounded-lg transform translate-y-[60px] translate-x-[20px] shadow-[0_40px_60px_rgba(0,0,0,0.8)] flex items-center justify-center transition-transform hover:translate-y-[50px] duration-500 z-0">
              <div className="absolute top-2 left-2 text-[10px] tracking-widest text-gray-500 uppercase">DMI Documentation</div>
              <div className="flex gap-2 opacity-30">
                <div className="w-8 h-12 border border-gray-400 rounded-sm"></div>
                <div className="w-8 h-12 border border-gray-400 rounded-sm"></div>
                <div className="w-8 h-12 border border-gray-400 rounded-sm"></div>
              </div>
              <div className="absolute left-1/2 bottom-[-10px] transform -translate-x-1/2 bg-gray-800 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg border border-gray-600">SECURITY</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MetricsBand = () => {
  return (
    <section className="bg-white border-b border-gray-100 py-12 relative z-20 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-[10px] font-bold tracking-[0.2em] text-gray-400 mb-8 uppercase text-center lg:text-left">Capability at a Glance</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 divide-x-0 lg:divide-x divide-gray-200">
          {[
            { value: '500+', label: 'Projects Completed' },
            { value: '21+', label: 'Years Experience' },
            { value: '12+', label: 'System Types', tooltip: 'System Types refer to the different technical technology categories DMI designs and coordinates, such as Audio/Video, Networking, Security, Lighting Control, and general Low Voltage systems.' },
            { value: '8+', label: 'Deliverable Types', tooltip: 'Deliverable Types are the actual final documents or outputs DMI hands over to the client, such as Floor Plans, Riser Diagrams, Rack Elevations, Material Takeoffs (BOMs), and As-Built drawings.' }
          ].map((metric, idx) => (
            <div key={idx} className={`flex flex-col items-center lg:items-start ${idx !== 0 ? 'lg:pl-8' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-[#E58A3A]"></div>
                <span className="text-4xl font-light text-[#101725]">{metric.value}</span>
              </div>
              <div className="flex items-center gap-1 group relative cursor-help">
                <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">{metric.label}</span>
                {metric.tooltip && (
                  <>
                    <Info size={14} className="text-gray-400 group-hover:text-[#E58A3A] transition-colors" />
                    <div className="absolute bottom-full left-1/2 lg:left-0 -translate-x-1/2 lg:translate-x-0 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-xl pointer-events-none text-center lg:text-left leading-relaxed">
                      {metric.tooltip}
                      <div className="absolute top-full left-1/2 lg:left-6 -translate-x-1/2 -mt-[1px] border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CapabilityMatrix = () => {
  const [selectedRow, setSelectedRow] = useState(0);

  return (
    <section className="bg-[#101725] text-white py-24 border-t border-[#1F2937]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-16">
          <h2 className="text-3xl lg:text-4xl font-light mb-6">One Team. <span className="text-[#E58A3A]">Multiple Technical Disciplines.</span></h2>
          <p className="text-gray-400 text-lg">
            From architectural plans to integrated technology systems, our capabilities span the documentation required to move projects from concept to construction.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 relative">
          {/* Main Table */}
          <div className="flex-grow overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-800 text-xs uppercase tracking-wider text-gray-500">
                  <th className="pb-4 font-medium w-1/3">Capability</th>
                  <th className="pb-4 font-medium text-center">Design</th>
                  <th className="pb-4 font-medium text-center">Drafting</th>
                  <th className="pb-4 font-medium text-center hidden sm:table-cell">Documentation</th>
                  <th className="pb-4 font-medium text-center">Coordination</th>
                </tr>
              </thead>
              <tbody>
                {matrixData.map((row, idx) => (
                  <tr
                    key={idx}
                    onClick={() => setSelectedRow(idx)}
                    className={`border-b border-gray-800/50 transition-all duration-300 cursor-pointer group ${selectedRow === idx ? 'bg-gray-800/40 border-l-2 border-l-[#E58A3A]' : 'hover:bg-gray-800/20 border-l-2 border-l-transparent'
                      }`}
                  >
                    <td className="py-5 pl-4 font-medium transition-colors flex items-center gap-3">
                      <span className={selectedRow === idx ? 'text-[#E58A3A]' : 'text-gray-300 group-hover:text-white'}>
                        {row.name}
                      </span>
                      <ChevronRight className={`w-4 h-4 transition-all ${selectedRow === idx ? 'opacity-100 text-[#E58A3A] translate-x-1' : 'opacity-0 text-gray-600 group-hover:opacity-50'}`} />
                    </td>
                    {row.checks.map((check, cIdx) => (
                      <td key={cIdx} className={`py-5 text-center ${cIdx === 2 ? 'hidden sm:table-cell' : ''}`}>
                        {check ? <Check className={`w-5 h-5 mx-auto transition-colors ${selectedRow === idx ? 'text-[#E58A3A]' : 'text-gray-600 group-hover:text-gray-400'}`} /> : <span className="text-gray-700">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Interactive Side Panel */}
          <div className="w-full lg:w-80 flex-shrink-0 relative hidden lg:block">
            <div className="sticky top-8 bg-[#070B14] border border-gray-800 p-8 rounded-xl min-h-[460px] transition-all duration-300 shadow-2xl flex flex-col">

              {/* Content based on selectedRow */}
              <div key={selectedRow} className="animate-in fade-in slide-in-from-right-4 duration-500 flex-grow">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[10px] font-bold text-[#E58A3A] tracking-[0.2em] uppercase">Detailed Scope</div>
                  <div className="w-8 h-[1px] bg-gray-700"></div>
                </div>
                <h3 className="text-xl font-medium text-white mb-6">{matrixData[selectedRow].name}</h3>

                <ul className="space-y-4 mb-8">
                  {matrixData[selectedRow].details.map((detail, dIdx) => (
                    <li key={dIdx} className="flex items-start gap-3 text-sm text-gray-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#E58A3A]/50 mt-1.5 flex-shrink-0"></div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Integrated Tools Stack */}
              <div key={`tools-${selectedRow}`} className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100 mt-auto pt-6 border-t border-gray-800/80">
                <div className="text-[10px] font-bold text-gray-500 tracking-[0.2em] uppercase mb-4">Technology Stack</div>
                <div className="flex flex-wrap gap-3">
                  {matrixData[selectedRow].tools.map((tool, tIdx) => {
                    const Icon = tool.icon;
                    return (
                      <div key={tIdx} className="group/tool relative">
                        {/* Glowing backdrop effect on hover */}
                        <div className="absolute inset-0 bg-[#E58A3A] opacity-0 group-hover/tool:opacity-20 blur-md transition-opacity duration-300 rounded"></div>
                        <div className="relative flex items-center gap-2 bg-[#101725] border border-gray-800 group-hover/tool:border-[#E58A3A]/50 transition-colors rounded px-3 py-2">
                          <Icon size={14} className="text-gray-500 group-hover/tool:text-[#E58A3A] transition-colors" />
                          <span className="text-xs font-mono text-gray-300 tracking-wide">{tool.name}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const OutputsSection = () => {
  const outputs = [
    { num: '01', title: 'Architectural Plans', desc: 'Detailed floor plans, elevations, and sections coordinated with technical systems.' },
    { num: '02', title: 'Low Voltage Plans', desc: 'Device locations, cable pathways, and system coordination overlays.' },
    { num: '03', title: 'Riser Diagrams', desc: 'Vertical system distribution, conduit sizing, and connectivity flow.' },
    { num: '04', title: 'Rack & Equipment Layouts', desc: 'Front/rear elevations, thermal management, and power distribution.' },
    { num: '05', title: 'Material Takeoffs', desc: 'Accurate BOMs generated directly from coordinated design models.' },
    { num: '06', title: 'As-Built Documentation', desc: 'Finalized documentation reflecting actual installed conditions for handover.' },
  ];

  return (
    <section className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <h2 className="text-3xl lg:text-4xl font-light text-[#101725] mb-4">From Information to <span className="font-semibold text-[#E58A3A]">Installation</span></h2>
          <p className="text-gray-500 text-lg">What we actually produce for our partners.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {outputs.map((item, idx) => (
            <div key={idx} className="group relative rounded-xl overflow-hidden bg-gray-50 border border-gray-200 aspect-[4/3] cursor-pointer">
              {/* Mock Drawing Background */}
              <div className="absolute inset-0 opacity-20 transition-opacity duration-500 group-hover:opacity-40"
                style={{
                  backgroundImage: 'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}>
                {/* Abstract geometric shapes mimicking plans */}
                <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 border-2 border-[#101725] bg-white rounded-sm"></div>
                <div className="absolute top-1/3 left-1/2 w-1/3 h-1/3 border-2 border-[#E58A3A] rounded-sm transform -translate-x-1/2"></div>
              </div>

              {/* Default State Content */}
              <div className="absolute inset-0 p-6 flex flex-col transition-opacity duration-300 group-hover:opacity-0">
                <span className="text-2xl font-light text-gray-300 mb-auto">{item.num}</span>
                <h3 className="text-xl font-medium text-[#101725] bg-white/80 inline-block px-2 py-1 -ml-2 rounded">{item.title}</h3>
              </div>

              {/* Hover State Content */}
              <div className="absolute inset-0 bg-[#101725]/95 p-6 flex flex-col justify-end text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <div className="mb-4">
                  <span className="text-[10px] font-bold text-[#E58A3A] tracking-widest uppercase block mb-2">{item.title}</span>
                  <div className="w-full h-[1px] bg-gray-700 mb-4"></div>
                  <p className="text-sm text-gray-300 leading-relaxed mb-6">{item.desc}</p>
                </div>
                <button className="flex items-center gap-2 text-sm font-medium text-[#E58A3A] hover:text-white transition-colors">
                  VIEW EXAMPLE <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SystemsStack = () => {
  const [activeLayer, setActiveLayer] = useState<number | null>(null);

  const systems = [
    { id: '01', name: 'ARCHITECTURE', desc: 'Base floorplans, structural boundaries, and elevations.' },
    { id: '02', name: 'LOW VOLTAGE', desc: 'Unified cable pathways, rough-ins, and panel schedules.' },
    { id: '03', name: 'AUDIO / VIDEO', desc: 'Signal flow, device placement, and complex rack elevations.' },
    { id: '04', name: 'NETWORK', desc: 'Switch topologies, VLAN planning, and WAP coverage maps.' },
    { id: '05', name: 'SECURITY', desc: 'Access control schematics, door details, and camera FOV.' },
    { id: '06', name: 'CONTROL', desc: 'Lighting load schedules, keypad engraving, and automation logic.' }
  ];

  return (
    <section className="bg-[#F7F4EF] py-24 overflow-hidden border-y border-gray-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16 lg:mb-24">
          <div className="text-[10px] font-bold tracking-[0.2em] text-[#E58A3A] mb-4 uppercase">System Integration</div>
          <h2 className="text-3xl lg:text-4xl font-light text-[#101725] mb-4">Where Capabilities <span className="font-semibold text-[#E58A3A]">Connect</span></h2>
          <p className="text-gray-500 text-lg">Every discipline is designed to overlay perfectly onto the architectural base, creating a unified and conflict-free technical package.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Side: Interactive List */}
          <div className="space-y-3 relative z-10 flex flex-col justify-center">
            {systems.map((sys, idx) => (
              <div
                key={idx}
                onMouseEnter={() => setActiveLayer(idx)}
                onMouseLeave={() => setActiveLayer(null)}
                className={`
                  p-5 rounded-lg border transition-all duration-300 cursor-default flex items-start gap-5
                  ${activeLayer === idx
                    ? 'bg-white border-[#E58A3A]/50 shadow-lg transform translate-x-2'
                    : activeLayer === null
                      ? 'bg-white/60 border-gray-200 hover:bg-white hover:border-gray-300'
                      : 'bg-white/30 border-transparent opacity-40 grayscale'
                  }
                `}
              >
                <span className={`font-mono text-sm mt-0.5 transition-colors duration-300 ${activeLayer === idx ? 'text-[#E58A3A]' : 'text-gray-400'}`}>
                  {sys.id}
                </span>
                <div>
                  <h4 className={`font-bold tracking-[0.15em] text-sm uppercase transition-colors duration-300 ${activeLayer === idx ? 'text-[#101725]' : 'text-gray-600'}`}>
                    {sys.name}
                  </h4>
                  <p className={`text-sm mt-1 transition-all duration-300 ${activeLayer === idx ? 'text-gray-600' : 'text-gray-500'}`}>
                    {sys.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Right Side: Visual Isometric Stack */}
          <div className="relative h-[500px] flex items-center justify-center lg:justify-end" style={{ perspective: '1200px' }}>
            <div className="relative w-full max-w-[320px] lg:max-w-[380px] aspect-square transition-transform duration-700 ease-out" style={{ transformStyle: 'preserve-3d', transform: 'rotateX(60deg) rotateZ(-45deg)' }}>
              {systems.map((sys, idx) => {
                const isActive = activeLayer === idx;
                const isHoveringAny = activeLayer !== null;

                // Base spacing spreads out when user interacts
                const baseSpacing = isHoveringAny ? 65 : 45;

                // Calculate Z position (Architecture at bottom, Control at top)
                let zOffset = idx * baseSpacing;

                // Highlight pop effect
                if (isActive) {
                  zOffset += 30;
                } else if (isHoveringAny && idx < activeLayer) {
                  // Push layers below the active one down slightly
                  zOffset -= 15;
                }

                return (
                  <div
                    key={idx}
                    className={`
                        absolute inset-0 border-2 rounded-xl transition-all duration-500 ease-out
                        flex items-center justify-center shadow-sm backdrop-blur-sm
                        ${isActive
                        ? 'border-[#E58A3A] bg-white/95 shadow-[0_30px_50px_rgba(229,138,58,0.25)]'
                        : isHoveringAny
                          ? 'border-[#101725]/10 bg-white/40'
                          : 'border-[#101725]/20 bg-white/70'
                      }
                      `}
                    style={{
                      transform: `translateZ(${zOffset}px)`,
                      opacity: isHoveringAny && !isActive ? 0.3 : 1,
                    }}
                  >
                    {/* Blueprint Grid Lines */}
                    <div className={`absolute inset-0 transition-opacity duration-300 rounded-xl overflow-hidden ${isActive ? 'opacity-20' : 'opacity-5'}`}
                      style={{
                        backgroundImage: `linear-gradient(${isActive ? '#E58A3A' : '#101725'} 1px, transparent 1px), linear-gradient(90deg, ${isActive ? '#E58A3A' : '#101725'} 1px, transparent 1px)`,
                        backgroundSize: '24px 24px'
                      }}>
                    </div>

                    {/* Floating Label (counter-rotated to face the user) */}
                    <div
                      className={`
                          absolute transition-all duration-500 font-bold tracking-[0.2em] text-xs lg:text-sm px-4 py-2 rounded-full backdrop-blur-md border
                          ${isActive ? 'bg-[#101725] text-white border-gray-700 opacity-100 translate-y-[-20px] scale-110' : 'bg-white/80 text-gray-400 border-gray-200 opacity-0 scale-90'}
                        `}
                      style={{
                        transform: 'rotateZ(45deg) rotateX(-60deg)',
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      {sys.name}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ProcessSection = () => {
  const steps = [
    { num: '01', title: 'Understand', desc: 'Review plans, requirements, specifications and project objectives.' },
    { num: '02', title: 'Develop', desc: 'Translate requirements into coordinated technical drawings.' },
    { num: '03', title: 'Review', desc: 'Coordinate revisions, details and integrate system requirements.' },
    { num: '04', title: 'Deliver', desc: 'Provide organized, accurate and field-ready documentation sets.' }
  ];

  return (
    <section className="bg-white py-24 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <h2 className="text-2xl lg:text-3xl font-light text-[#101725]">How We Turn Requirements Into <span className="font-semibold text-[#E58A3A]">Documentation</span></h2>
        </div>

        <div className="relative">
          {/* Continuous connecting line (Desktop) */}
          <div className="hidden lg:block absolute top-12 left-6 right-6 h-[2px] bg-gray-100 z-0">
            <div className="absolute top-0 left-0 h-full bg-[#E58A3A] w-1/3 opacity-50"></div>
          </div>

          <div className="grid lg:grid-cols-4 gap-8 lg:gap-12 relative z-10">
            {steps.map((step, idx) => (
              <div key={idx} className="relative group">
                <div className="bg-white border-2 border-gray-100 w-16 h-16 rounded-full flex items-center justify-center text-xl font-light text-gray-400 mb-6 group-hover:border-[#E58A3A] group-hover:text-[#E58A3A] transition-colors shadow-sm">
                  {step.num}
                </div>
                <h3 className="text-lg font-medium text-[#101725] mb-3">{step.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed border-l-2 border-gray-100 pl-4 group-hover:border-[#E58A3A]/30 transition-colors">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TechSection = () => {
  const tools = [
    { name: 'AutoCAD', desc: 'Precision drafting & documentation' },
    { name: 'Revit', desc: 'BIM coordination & spatial docs' },
    { name: 'DIALux', desc: 'Lighting design & analysis' },
    { name: 'SketchUp', desc: '3D spatial visualization' },
    { name: 'D-Tools', desc: 'System design & proposals' },
  ];

  return (
    <section className="bg-[#101725] py-24 text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <h2 className="text-2xl font-light mb-12">Tools Behind the Work</h2>
        <div className="flex flex-wrap gap-4">
          {tools.map((tool, idx) => (
            <div key={idx} className="bg-[#070B14] border border-gray-800 rounded-lg px-6 py-4 flex-grow min-w-[200px] hover:border-[#E58A3A]/50 transition-colors">
              <h4 className="text-white font-medium mb-1">{tool.name}</h4>
              <p className="text-xs text-gray-500">{tool.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CertificationsSection = () => {
  const [activeCert, setActiveCert] = useState(0);

  const certData = [
    {
      brand: 'Savant',
      total: '4+',
      description: 'Our Savant certified programmers handle complex residential and commercial deployments, ensuring seamless UI generation and robust backend control.',
      breakdown: [
        { title: 'Core Programmer', count: 2, desc: 'Foundation system programming, deployment, and Blueprint configuration.' },
        { title: 'Advanced Integration', count: 1, desc: 'Deep ecosystem integration, custom UI flows, and third-party API mapping.' },
        { title: 'Lighting & Shades', count: 1, desc: 'Advanced environmental control setup and scene coordination.' }
      ]
    },
    {
      brand: 'Lutron',
      total: '3+',
      description: 'DMI provides comprehensive lighting control documentation, load schedule generation, and programming parameters for high-end Lutron systems.',
      breakdown: [
        { title: 'Homeworks QS', count: 1, desc: 'Luxury residential lighting control and motorized shading design.' },
        { title: 'RadioRA 3', count: 2, desc: 'Wireless and hybrid lighting control system deployment.' }
      ]
    },
    {
      brand: 'Q-SYS',
      total: '5+',
      description: 'Extensive capability in DSP design, audio routing, and control logic for enterprise and commercial environments.',
      breakdown: [
        { title: 'Level 1 Designer', count: 3, desc: 'Core audio, video, and control design within Q-SYS Designer Software.' },
        { title: 'Level 2 Programmer', count: 1, desc: 'Advanced control components, custom scripting, and complex UI design.' },
        { title: 'Quantum Control', count: 1, desc: 'Enterprise-grade control processing and third-party device management.' }
      ]
    },
    {
      brand: 'Crestron',
      total: '2+',
      description: 'Technical coordination and schematic design for Crestron ecosystems, ensuring accurate cable paths and device compatibility.',
      breakdown: [
        { title: 'Crestron Home', count: 1, desc: 'Streamlined residential deployment and configuration.' },
        { title: 'NVX Design', count: 1, desc: 'AV-over-IP network topology and endpoint layout.' }
      ]
    },
    {
      brand: 'MXNet',
      total: '1+',
      description: 'Specialized knowledge in AV-over-IP distribution, switch configuration, and multicast network requirements.',
      breakdown: [
        { title: 'AVoIP Specialist', count: 1, desc: 'System design, bandwidth calculation, and network switch configuration.' }
      ]
    }
  ];

  return (
    <section className="bg-[#070B14] py-24 text-white border-t border-[#1F2937]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-light mb-2">Certified <span className="font-semibold text-[#E58A3A]">Expertise.</span></h2>
          <p className="text-lg text-gray-500">Verified qualifications ensuring precise technical coordination.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 max-w-6xl mx-auto">

          {/* Left Navigation / Index */}
          <div className="w-full lg:w-1/3 flex flex-col border border-gray-800 rounded-xl overflow-hidden bg-[#101725]">
            <div className="px-6 py-4 border-b border-gray-800 bg-[#0a0f1a] flex justify-between items-center">
              <span className="text-xs font-bold tracking-widest text-gray-500 uppercase">Manufacturer</span>
              <span className="text-xs font-bold tracking-widest text-gray-500 uppercase">Certs</span>
            </div>

            <div className="flex flex-col">
              {certData.map((cert, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveCert(idx)}
                  className={`px-6 py-5 flex items-center justify-between transition-all border-l-2 text-left
                    ${activeCert === idx
                      ? 'bg-gray-800/40 border-l-[#E58A3A] text-white'
                      : 'border-l-transparent text-gray-400 hover:bg-gray-800/20 hover:text-gray-200'
                    } ${idx !== certData.length - 1 ? 'border-b border-gray-800/50' : ''}
                  `}
                >
                  <span className="text-lg font-medium">{cert.brand}</span>
                  <div className={`flex items-center gap-3 transition-colors ${activeCert === idx ? 'text-[#E58A3A]' : 'text-gray-600'}`}>
                    <span className="font-mono text-xl">{cert.total}</span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${activeCert === idx ? 'translate-x-1' : ''}`} />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right Detail Panel */}
          <div className="w-full lg:w-2/3">
            <div className="bg-[#101725] border border-gray-800 rounded-xl p-8 lg:p-10 min-h-[480px] relative overflow-hidden">
              {/* Subtle background icon */}
              <Award className="absolute -bottom-10 -right-10 w-64 h-64 text-gray-800/20 pointer-events-none" />

              <div key={activeCert} className="animate-in fade-in slide-in-from-right-4 duration-500 relative z-10 flex flex-col h-full">

                <div className="flex items-center gap-4 mb-6">
                  <h3 className="text-3xl font-light text-white">{certData[activeCert].brand}</h3>
                  <div className="px-3 py-1 bg-[#E58A3A]/10 border border-[#E58A3A]/30 rounded text-[#E58A3A] text-sm font-mono font-medium">
                    {certData[activeCert].total} CERTIFICATIONS
                  </div>
                </div>

                <p className="text-gray-400 text-lg leading-relaxed mb-10 max-w-2xl">
                  {certData[activeCert].description}
                </p>

                <div className="text-[10px] font-bold text-gray-500 tracking-[0.2em] uppercase mb-4 border-b border-gray-800 pb-2">
                  Credential Breakdown
                </div>

                <div className="grid gap-4 mt-2">
                  {certData[activeCert].breakdown.map((item, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:items-start gap-4 p-4 rounded-lg bg-[#070B14] border border-gray-800/80 hover:border-[#E58A3A]/30 transition-colors">
                      <div className="bg-[#101725] border border-gray-700 w-10 h-10 rounded flex items-center justify-center flex-shrink-0">
                        <span className="text-[#E58A3A] font-mono font-bold">{item.count}x</span>
                      </div>
                      <div>
                        <h4 className="text-white font-medium mb-1 flex items-center gap-2">
                          {item.title}
                        </h4>
                        <p className="text-sm text-gray-500 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            </div>
          </div>

        </div>

        <div className="pt-16 mt-8 border-t border-gray-800 flex flex-wrap justify-center gap-4 text-xs tracking-widest text-gray-600 uppercase">
          <span>Certified Professionals</span>
          <span className="hidden sm:inline">·</span>
          <span>Manufacturer Training</span>
          <span className="hidden sm:inline">·</span>
          <span>Continuous Learning</span>
          <span className="hidden sm:inline">·</span>
          <span>Remote Support</span>
        </div>
      </div>
    </section>
  );
};

const PortfolioSection = () => {
  const [activeFilter, setActiveFilter] = useState('ALL');
  const filters = ['ALL', 'ARCHITECTURAL', 'LOW VOLTAGE', 'SCHEMATICS', 'LIGHTING', 'NETWORK'];

  return (
    <section className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <h2 className="text-2xl font-light text-[#101725] mb-8 text-center">See the Capability in Practice</h2>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {filters.map(filter => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-4 py-2 text-xs font-medium tracking-wider uppercase rounded-full transition-colors ${activeFilter === filter
                  ? 'bg-[#101725] text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Minimal Grid placeholder to connect to portfolio */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="aspect-video bg-gray-100 rounded border border-gray-200 relative overflow-hidden group cursor-pointer">
              <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url('https://placehold.co/600x400/eeeeee/cccccc?text=Portfolio+${i}')` }}></div>
              <div className="absolute inset-0 bg-[#101725]/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-[#E58A3A] text-sm font-medium">View Project</span>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <button className="text-[#101725] font-medium border-b border-[#101725] pb-1 hover:text-[#E58A3A] hover:border-[#E58A3A] transition-colors">
            View Full Portfolio
          </button>
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  return (
    <section className="bg-[#101725] py-24 border-t border-[#E58A3A]/20">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="text-[#E58A3A] text-xs font-bold tracking-[0.2em] uppercase mb-4">Ready to add technical capacity?</div>
        <h2 className="text-4xl lg:text-5xl font-light text-white mb-6">
          Bring Your Next Project <br />
          <span className="font-semibold text-[#E58A3A]">Into Focus.</span>
        </h2>
        <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
          Whether you need drafting support, low-voltage documentation, takeoffs or complete technical design support, DMI can work alongside your team from project start to final documentation.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <button className="bg-[#E58A3A] text-white px-8 py-3 rounded text-sm font-medium hover:bg-[#F19A45] transition-colors flex items-center justify-center gap-2">
            Request a Quote <ArrowRight className="w-4 h-4" />
          </button>
          <button className="bg-transparent border border-gray-600 text-white px-8 py-3 rounded text-sm font-medium hover:border-gray-400 transition-colors">
            Contact Our Team
          </button>
        </div>
      </div>
    </section>
  );
};

import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function SkillsPage() {
  return (
    <div className="font-sans antialiased text-gray-900 bg-white min-h-screen">
      <Header />
      <HeroSection />
      <MetricsBand />
      <CapabilityMatrix />
      <OutputsSection />
      <CertificationsSection />
      <SystemsStack />
      <ProcessSection />
      <TechSection />
      <PortfolioSection />
      <CTASection />
      <Footer />
    </div>
  );
}
